import { useId, useState } from "react";
import type { VerificationAnswer } from "@ondo/shared";

import { ANSWERS } from "./verifications.model.ts";
import type { ClaimCard } from "./verifications.model.ts";
import { ANSWER_COMPOSERS, ANSWER_LABELS, VERIFICATION_COPY } from "./verifications.constants.ts";

type Props = {
  claims: ClaimCard[];
  busy: boolean;
  onAnswer: (claimId: string, body: { answer: VerificationAnswer; body?: string | null }) => void;
};

export function AffiliationClaims({ claims, busy, onAnswer }: Props) {
  const [open, setOpen] = useState<{ claimId: string; answer: VerificationAnswer } | null>(
    null,
  );
  const [text, setText] = useState("");
  const fieldId = useId();

  return (
    <ul className="verifications__list">
      {claims.map((claim) => (
        <li
          key={claim.claimId}
          className={`verifications__card ${
            claim.awaiting ? "verifications__card--awaiting" : ""
          }`}
        >
          <header className="verifications__card-head">
            <div className="verifications__card-title">
              <h3>{claim.title}</h3>
              <p>
                {claim.claimedBy
                  ? `${claim.claimedBy.name} · ${claim.claimedBy.ondoId}`
                  : VERIFICATION_COPY.withheld}
                {claim.period ? ` · ${claim.period}` : ""}
              </p>
            </div>
            <span
              className={`verifications__stamp verifications__stamp--${
                claim.awaiting ? "awaiting" : "resolved"
              }`}
            >
              {claim.awaiting ? VERIFICATION_COPY.awaitingBadge : claim.outcome}
            </span>
          </header>

          <section className="verifications__asked">
            <h4>{VERIFICATION_COPY.whatToVerify}</h4>
            <p>
              {claim.note ??
                `That this is a real affiliation with ${claim.subject.name}.`}
            </p>
          </section>

          {claim.canAnswer && open?.claimId !== claim.claimId && (
            <div className="verifications__buttons">
              {ANSWERS.map((answer) => (
                <button
                  key={answer}
                  type="button"
                  className={`verifications__button verifications__button--${answer}`}
                  onClick={() => {
                    setText("");
                    setOpen({ claimId: claim.claimId, answer });
                  }}
                >
                  {ANSWER_LABELS[answer]}
                </button>
              ))}
            </div>
          )}

          {open?.claimId === claim.claimId && (
            <div className="verifications__composer">
              <label htmlFor={fieldId}>{ANSWER_COMPOSERS[open.answer].label}</label>
              <textarea
                id={fieldId}
                rows={3}
                value={text}
                placeholder={ANSWER_COMPOSERS[open.answer].placeholder}
                onChange={(event) => setText(event.target.value)}
              />
              <div className="verifications__buttons">
                <button
                  type="button"
                  className="verifications__button"
                  onClick={() => setOpen(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="verifications__button verifications__button--send"
                  disabled={
                    busy ||
                    (ANSWER_COMPOSERS[open.answer].required && text.trim().length === 0)
                  }
                  onClick={() => {
                    onAnswer(claim.claimId, {
                      answer: open.answer,
                      body: text.trim() || null,
                    });
                    setOpen(null);
                  }}
                >
                  {ANSWER_LABELS[open.answer]}
                </button>
              </div>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
